import { useState, useEffect } from 'react';
import { Plus, Copy, Trash2, Eye, EyeOff, Key } from 'lucide-react';
import { supabaseClient } from '../../lib/supabase';
import { LoadingScreen } from '../common/LoadingScreen';
import { toast } from '../../lib/toast';

interface ApiKey {
  id: string;
  name: string;
  key: string;
  project_id: string | null;
  is_active: boolean;
  created_at: string;
  expires_at: string | null;
  project?: { id: string; name: string } | null;
}

interface Project {
  id: string;
  name: string;
}

function generateApiKey(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = 'ycp_';
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export default function ApiKeysPage() {
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());

  // Create form state
  const [newName, setNewName] = useState('');
  const [newProjectId, setNewProjectId] = useState<string>('');
  const [newExpiresAt, setNewExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [keysRes, projectsRes] = await Promise.all([
        supabaseClient
          .from('api_keys')
          .select('*, project:projects(id, name)')
          .order('created_at', { ascending: false }),
        supabaseClient
          .from('projects')
          .select('id, name')
          .order('name'),
      ]);

      if (keysRes.error) throw keysRes.error;
      if (projectsRes.error) throw projectsRes.error;

      setKeys(keysRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (err) {
      console.error('Error loading API keys:', err);
      toast.error('Не вдалося завантажити API ключі');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Вкажіть назву ключа');
      return;
    }

    try {
      setSaving(true);
      const { data: userData } = await supabaseClient.auth.getUser();

      const { error } = await supabaseClient
        .from('api_keys')
        .insert({
          name: newName.trim(),
          key: generateApiKey(),
          project_id: newProjectId || null,
          is_active: true,
          created_by: userData.user?.id || null,
          expires_at: newExpiresAt || null,
        });

      if (error) throw error;

      toast.success('API ключ створено');
      setShowCreate(false);
      setNewName('');
      setNewProjectId('');
      setNewExpiresAt('');
      await loadData();
    } catch (err) {
      console.error('Error creating API key:', err);
      toast.error('Не вдалося створити API ключ');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (apiKey: ApiKey) => {
    try {
      const { error } = await supabaseClient
        .from('api_keys')
        .update({ is_active: !apiKey.is_active })
        .eq('id', apiKey.id);

      if (error) throw error;
      toast.success(apiKey.is_active ? 'Ключ деактивовано' : 'Ключ активовано');
      await loadData();
    } catch (err) {
      console.error('Error toggling API key:', err);
      toast.error('Не вдалося оновити статус ключа');
    }
  };

  const handleDelete = async (apiKey: ApiKey) => {
    if (!confirm(`Видалити ключ "${apiKey.name}"? Цю дію неможливо скасувати.`)) return;

    try {
      const { error } = await supabaseClient
        .from('api_keys')
        .delete()
        .eq('id', apiKey.id);

      if (error) throw error;
      toast.success('Ключ видалено');
      await loadData();
    } catch (err) {
      console.error('Error deleting API key:', err);
      toast.error('Не вдалося видалити ключ');
    }
  };

  const handleCopy = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success('Ключ скопійовано');
  };

  const toggleKeyVisibility = (id: string) => {
    setVisibleKeys(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const maskKey = (key: string) => {
    return key.substring(0, 8) + '••••••••••••••••••••••••••••';
  };

  if (loading) return <LoadingScreen />;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://your-project.supabase.co';

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">API Ключі</h2>
            <p className="text-sm text-gray-500 mt-1">
              Ключі для доступу зовнішніх сервісів до API
            </p>
          </div>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Створити ключ
          </button>
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="mb-6 p-4 border border-indigo-200 rounded-lg bg-indigo-50">
            <h3 className="text-sm font-medium text-gray-800 mb-3">Новий API ключ</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Назва *</label>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="N8N Integration"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Проект</label>
                <select
                  value={newProjectId}
                  onChange={e => setNewProjectId(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">Глобальний (всі проекти)</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Термін дії</label>
                <input
                  type="date"
                  value={newExpiresAt}
                  onChange={e => setNewExpiresAt(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="mt-4 flex space-x-2">
              <button
                onClick={handleCreate}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm disabled:opacity-50"
              >
                {saving ? 'Створення...' : 'Створити'}
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
              >
                Скасувати
              </button>
            </div>
          </div>
        )}

        {/* Keys list */}
        {keys.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Key className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Немає API ключів</p>
            <p className="text-sm mt-1">Створіть ключ для підключення зовнішніх сервісів</p>
          </div>
        ) : (
          <div className="space-y-3">
            {keys.map(apiKey => (
              <div
                key={apiKey.id}
                className={`border rounded-lg p-4 ${apiKey.is_active ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-60'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-sm font-medium text-gray-900">{apiKey.name}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        apiKey.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {apiKey.is_active ? 'Активний' : 'Неактивний'}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        apiKey.project_id ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {apiKey.project_id
                          ? `Проект: ${apiKey.project?.name || apiKey.project_id}`
                          : 'Глобальний'}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center space-x-2">
                      <code className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {visibleKeys.has(apiKey.id) ? apiKey.key : maskKey(apiKey.key)}
                      </code>
                      <button
                        onClick={() => toggleKeyVisibility(apiKey.id)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title={visibleKeys.has(apiKey.id) ? 'Сховати' : 'Показати'}
                      >
                        {visibleKeys.has(apiKey.id) ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleCopy(apiKey.key)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Копіювати"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-1 text-xs text-gray-400">
                      Створено: {new Date(apiKey.created_at).toLocaleDateString('uk-UA')}
                      {apiKey.expires_at && (
                        <span className={new Date(apiKey.expires_at) < new Date() ? 'text-red-500 ml-2' : 'ml-2'}>
                          Діє до: {new Date(apiKey.expires_at).toLocaleDateString('uk-UA')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleActive(apiKey)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        apiKey.is_active ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          apiKey.is_active ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => handleDelete(apiKey)}
                      className="p-2 text-red-400 hover:text-red-600"
                      title="Видалити"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Usage documentation */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-800 mb-2">Як використовувати</h3>
          <p className="text-sm text-gray-600 mb-3">
            Відправляйте POST-запити з API-ключем у заголовку <code className="bg-gray-200 px-1 rounded">X-API-Key</code>:
          </p>
          <pre className="bg-gray-900 text-green-400 p-3 rounded-md text-xs overflow-x-auto">
{`curl -X POST ${supabaseUrl}/functions/v1/external-api \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ycp_your_key_here" \\
  -d '{
    "keyword": "seo оптимізація",
    "project_id": "uuid-проекту",
    "url": "https://mysite.com/page",
    "location": "2804",
    "language": "uk",
    "additional_keywords": ["seo", "оптимізація"],
    "priority": 5
  }'`}
          </pre>
          <p className="text-xs text-gray-500 mt-2">
            Для ключів прив'язаних до проекту — <code className="bg-gray-200 px-1 rounded">project_id</code> не потрібен.
            Пріоритет: 1-10 (за замовчуванням 5). Локація за замовчуванням: Україна (2804).
          </p>
        </div>
      </div>
    </div>
  );
}
